/*
 * The timeline's own HTTP calls (protocol.md §3.2): history, the composer's markdown preview, the
 * two routes of the draft mirror — the warm-boot read and the flush that carries a half-written
 * message out of a page that is going away — and the picker's recently used emoji. Sending, editing
 * and deleting are socket types and the sidebar arrives in the bootstrap, so a conversation needs
 * nothing else over HTTP; the routes that *make* and *change* a conversation, and the reads of its
 * own views, live beside them in `conversation/api.ts`. Everything goes through core's `api`, which
 * is where the CSRF header, `no-store` and the one error type are decided, and every route of the
 * module is called from a file like this one and from no component (`api.test.ts`).
 *
 * The mirror's two socket requests stand here as well, beside the routes they belong with: the
 * transport at the bottom of this file is the whole of what `core/drafts/mirror.ts` knows about chat,
 * because core may not name a module's route or a module's request type (architecture.md D21).
 */

import type { DraftMirrorTransport, MirrorDraft, MirrorPut } from "../../core/drafts/mirror.ts"
import type { WireTarget } from "../../core/drafts/targetKey.ts"
import { api, csrfValue } from "../../core/net/api.ts"
import type { Render } from "../../core/render/nodes.ts"
import type { DraftListResult, DraftView, EmojiRecents, MentionRef } from "./wire/chat.gen.ts"
import type { ChatRequests } from "./wire/requests.ts"
import type { HistoryPage } from "./wire/views.ts"

/**
 * The server's own page size cap (`Limits.historyPageMax`); a request never asks for more. The
 * loaders read it too: whether the first unread can be inside the tail is a question of this number.
 */
export const HISTORY_PAGE = 50

function historyPath(conv: number, query: Record<string, number | undefined>): string {
	const params = new URLSearchParams()
	for (const [key, value] of Object.entries(query)) {
		if (value !== undefined) params.set(key, String(value))
	}
	params.set("limit", String(HISTORY_PAGE))
	return `/api/chat/conversations/${conv}/messages?${params.toString()}`
}

/** The newest page of a conversation — what opening it shows. */
export function fetchTail(conv: number): Promise<HistoryPage> {
	return api.get<HistoryPage>(historyPath(conv, {}))
}

/** The page above a loaded window, for scrolling into the past. */
export function fetchBefore(conv: number, before: number): Promise<HistoryPage> {
	return api.get<HistoryPage>(historyPath(conv, { before }))
}

/** The page below a loaded window — walking back down after a permalink jump. */
export function fetchAfter(conv: number, after: number): Promise<HistoryPage> {
	return api.get<HistoryPage>(historyPath(conv, { after }))
}

/**
 * The page around one message — a permalink jump or a landing on the first unread, either of which
 * may leave newer messages unloaded. The anchor is a range boundary on the server, so a seq that no
 * longer exists still answers with what stands around it.
 */
export function fetchAround(conv: number, around: number): Promise<HistoryPage> {
	return api.get<HistoryPage>(historyPath(conv, { around }))
}

export interface PreviewResult {
	render: Render
	mentions: MentionRef[]
	limits: { maxChars: number }
}

/**
 * The composer's preview: the server parses, exactly as it will at send time, and the timeline's
 * own renderer draws the result — so a preview can never show something the message will not be
 * (spec 06 §3.4, spec 12 §4.4). Membership is checked there, which is why the conversation travels
 * with the body.
 */
export function previewMarkdown(conv: number, markdown: string): Promise<PreviewResult> {
	return api.post<PreviewResult>("/api/chat/markdown/preview", { conv, markdown })
}

/**
 * The recently used emoji and the skin tone, as the server keeps them for the person
 * (`core/emoji/recents.ts` holds them for the tab, and `bind.ts` hands it this read).
 */
export function fetchEmojiRecents(): Promise<EmojiRecents> {
	return api.get<EmojiRecents>("/api/chat/emoji/recents")
}

// ---- the draft mirror (protocol.md §2.5.6) ----

/** The flush route: the one HTTP write on the live path, because `sendBeacon` cannot set a header. */
const FLUSH_PATH = "/api/chat/drafts/flush"

/** The two socket requests the mirror makes, as the boot's typed `send` answers them. */
type DraftRequest = <T extends "chat.draft.put" | "chat.draft.get">(
	type: T,
	data: ChatRequests[T][0],
) => Promise<ChatRequests[T][1]>

/**
 * One draft as the mirror reads it. The generated struct types `targetKind` as a `string` — tygo emits
 * a Go const block that way — so the three spellings the protocol names are checked here, and a row
 * with any other kind is not a draft this client can place.
 */
function mirrorDraft(view: DraftView): MirrorDraft | null {
	if (
		view.targetKind !== "conversation" &&
		view.targetKind !== "thread" &&
		view.targetKind !== "messageEdit"
	) {
		return null
	}
	return { ...view, targetKind: view.targetKind }
}

/**
 * What `core/drafts/mirror.ts` reaches the server through. It is built once at boot with the socket's
 * own `send`, and it holds no state: which draft is offered, when, and what a conflict means are the
 * mirror's decisions, and this is the adapter that carries them out.
 */
export function draftMirrorTransport(request: DraftRequest): DraftMirrorTransport {
	return {
		// The warm-boot read: the mirror reconciles before the socket is necessarily useful, and a GET
		// needs no token of its own.
		list: async () => {
			const answer = await api.get<DraftListResult>("/api/chat/drafts")
			const drafts: MirrorDraft[] = []
			for (const view of answer.drafts) {
				const draft = mirrorDraft(view)
				if (draft !== null) drafts.push(draft)
			}
			return { drafts, tombstones: answer.tombstones.filter(isMirrorTarget) }
		},
		get: async (target: WireTarget) => {
			const answer = await request("chat.draft.get", target)
			return answer.draft === undefined || answer.draft === null ? null : mirrorDraft(answer.draft)
		},
		put: (put: MirrorPut) => request("chat.draft.put", { ...put, attachments: put.attachments }),
		beacon: (puts) => {
			// A beacon outlives the document; a fetch from a page that is unloading does not. The token
			// travels in the body because no header can be set on it (security.md §7).
			const body = JSON.stringify({ csrfToken: csrfValue(), drafts: puts })
			navigator.sendBeacon?.(FLUSH_PATH, new Blob([body], { type: "application/json" }))
		},
		flush: async (puts) => {
			await api.post<void>(FLUSH_PATH, { csrfToken: csrfValue(), drafts: puts })
		},
	}
}

/** A tombstone whose target kind is one of the three the protocol names. */
function isMirrorTarget<T extends { targetKind: string }>(
	row: T,
): row is T & { targetKind: "conversation" | "thread" | "messageEdit" } {
	return (
		row.targetKind === "conversation" ||
		row.targetKind === "thread" ||
		row.targetKind === "messageEdit"
	)
}
